import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherPortalEnhanced = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  // State for AI Generation
  const [generationParams, setGenerationParams] = useState({
    discipline: 'Physics',
    topic: '',
    subskills: '',
    cognitive_process: 'apply,justify',
    difficulty: 'medium',
    n_pairs: 1
  });
  const [generatedPairs, setGeneratedPairs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  
  // State for Manual Submission
  const [manualQuestion, setManualQuestion] = useState({
    subject: 'Physics',
    exam_context: ['TYT'],
    language: 'TR',
    difficulty: 'medium',
    topic_tags: '',
    skills: '',
    question_type: 'MCQ',
    stem: '',
    options: ['', '', '', '', ''],
    correct_answer: 'A',
    solution_short: ''
  });
  
  // State for submissions
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('ai-generator'); // ai-generator, manual, my-questions

  useEffect(() => {
    if (user && (user.role === 'teacher' || user.role === 'admin')) {
      fetchMySubmissions();
    }
  }, [user]);

  const fetchMySubmissions = async () => {
    try {
      const response = await axios.get(`${API}/orchestrator-v3/teacher/my-submissions`, {
        withCredentials: true
      });
      
      // All submissions from database (pending, approved, rejected)
      const submissions = response.data.submissions || [];
      
      setMySubmissions(submissions);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setMySubmissions([]);
    }
  };

  const handleGeneratePairs = async () => {
    setGenerating(true);
    setGenerationError('');
    setGeneratedPairs([]);
    
    try {
      const response = await axios.post(
        `${API}/teacher/generate-pair`,
        {
          discipline: generationParams.discipline,
          topic: generationParams.topic,
          subskills: generationParams.subskills.split(',').map(s => s.trim()),
          cognitive_process: generationParams.cognitive_process.split(',').map(s => s.trim()),
          difficulty: generationParams.difficulty,
          n_pairs: parseInt(generationParams.n_pairs)
        },
        { withCredentials: true }
      );
      
      setGeneratedPairs(response.data.pairs || []);
      setSuccess(language === 'tr' 
        ? `✅ ${response.data.count} soru çifti oluşturuldu!` 
        : `✅ ${response.data.count} question pairs generated!`
      );
    } catch (error) {
      console.error('Generation error:', error);
      setGenerationError(
        error.response?.data?.detail || 
        (language === 'tr' ? 'Soru oluşturulurken hata oluştu' : 'Error generating questions')
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmitGeneratedPair = async (pair) => {
    setLoading(true);
    try {
      // Transform AI-generated pair to submission format
      const submissionPayload = {
        subject: pair.TYT_item?.subject || generationParams.discipline,
        topic: pair.TYT_item?.topic_tags?.[0] || generationParams.topic,
        pair_title: pair.concept_summary || 'Generated Question Pair',
        TYT_item_raw: {
          type: 'MCQ',
          stem_TR: pair.TYT_item?.stem_TR || '',
          options: pair.TYT_item?.options || [],
          correct_option: pair.TYT_item?.correct_option || 'A',
          solution_TR: pair.TYT_item?.solution_short_TR || ''
        },
        AP_item_raw: {
          type: 'FRQ',
          stem_EN: pair.AP_item?.stem_EN || '',
          rubric_points: pair.AP_item?.rubric_points || [],
          model_response_EN: pair.AP_item?.model_response_EN || ''
        }
      };
      
      console.log('[SUBMIT] Sending submission payload:', submissionPayload);
      
      const response = await axios.post(
        `${API}/orchestrator-v3/teacher/submit-pair`,
        submissionPayload,
        { withCredentials: true }
      );
      
      console.log('[SUBMIT] Server response:', response.data);
      
      setSuccess(language === 'tr' 
        ? '✅ Soru çifti onay için gönderildi!' 
        : '✅ Question pair submitted for approval!'
      );
      
      // Remove from generated pairs
      setGeneratedPairs(generatedPairs.filter(p => p.pair_id !== pair.pair_id));
      fetchMySubmissions();
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = error.response?.data?.hint_TR || error.response?.data?.detail || 
        (language === 'tr' ? 'Gönderim hatası! Lütfen tüm alanları kontrol edin.' : 'Submission failed! Please check all fields.');
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitManualQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Parse topic_tags and skills
      const topic_tags = manualQuestion.topic_tags.split(',').map(s => s.trim()).filter(s => s);
      const skills = manualQuestion.skills.split(',').map(s => s.trim()).filter(s => s);
      
      // Build TYT item (Turkish, MCQ)
      const tyt_item = {
        language: "TR",
        question_type: "MCQ",
        difficulty: manualQuestion.difficulty,
        stem_TR: manualQuestion.stem,
        options: manualQuestion.options.filter(o => o).map(opt => opt),
        correct_option: manualQuestion.correct_answer,
        solution_short_TR: manualQuestion.solution_short,
        topic_tags: topic_tags,
        skills: skills,
        bloom: "understand",
        exam_context: "TYT"
      };
      
      // Build AP item (English, open-ended - use same stem translated or similar)
      const ap_item = {
        language: "EN",
        question_type: "open_ended",
        difficulty: manualQuestion.difficulty,
        stem_EN: manualQuestion.stem, // Teacher can provide English version later
        rubric_points: [
          "State the concept clearly",
          "Provide logical reasoning",
          "Include supporting details"
        ],
        model_response_EN: manualQuestion.solution_short || "Model response to be provided",
        topic_tags: topic_tags,
        skills: skills,
        bloom: "analyze",
        exam_context: "AP"
      };
      
      // Submit as TYT-AP pair to V3 endpoint
      const payload = {
        TYT_item: tyt_item,
        AP_item: ap_item,
        concept_summary: `${manualQuestion.subject} - ${topic_tags.join(', ')}`
      };
      
      await axios.post(
        `${API}/orchestrator-v3/teacher/submit-pair`,
        payload,
        { withCredentials: true }
      );
      
      setSuccess(language === 'tr' 
        ? '✅ Soru çifti onay için gönderildi!' 
        : '✅ Question pair submitted for approval!'
      );
      
      // Reset form
      setManualQuestion({
        subject: 'Physics',
        exam_context: ['TYT'],
        language: 'TR',
        difficulty: 'medium',
        topic_tags: '',
        skills: '',
        question_type: 'MCQ',
        stem: '',
        options: ['', '', '', '', ''],
        correct_answer: 'A',
        solution_short: ''
      });
      
      fetchMySubmissions();
    } catch (error) {
      console.error('Submission error:', error);
      const errorMsg = error.response?.data?.hint_TR || error.response?.data?.detail || 
        (language === 'tr' ? 'Gönderim hatası! Lütfen tüm alanları kontrol edin.' : 'Submission failed! Please check all fields.');
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'mentor') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-xl text-gray-600">
            {language === 'tr' ? 'Bu sayfaya erişim yetkiniz yok.' : 'No access to this page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Öğretmen Portalı' : 'Teacher Portal'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
            {success}
            <button onClick={() => setSuccess('')} className="float-right text-green-600 hover:text-green-800">✕</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('ai-generator')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'ai-generator'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🤖 {language === 'tr' ? 'AI Soru Üretici' : 'AI Question Generator'}
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'manual'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            ✍️ {language === 'tr' ? 'Manuel Soru Ekle' : 'Manual Question Entry'}
          </button>
          <button
            onClick={() => setActiveTab('my-questions')}
            className={`px-6 py-3 font-semibold ${
              activeTab === 'my-questions'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 {language === 'tr' ? 'Sorularım' : 'My Questions'} ({mySubmissions.length})
          </button>
        </div>

        {/* AI Generator Tab */}
        {activeTab === 'ai-generator' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                🤖 {language === 'tr' ? 'TYT-AP Soru Çifti Üret' : 'Generate TYT-AP Question Pairs'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Disiplin' : 'Discipline'}
                  </label>
                  <select
                    value={generationParams.discipline}
                    onChange={(e) => setGenerationParams({...generationParams, discipline: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Physics">Physics</option>
                    <option value="Calculus">Calculus</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Biology">Biology</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Konu' : 'Topic'}
                  </label>
                  <input
                    type="text"
                    value={generationParams.topic}
                    onChange={(e) => setGenerationParams({...generationParams, topic: e.target.value})}
                    placeholder="e.g., Newton's Second Law in 1D"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Alt Beceriler (virgülle ayırın)' : 'Sub-skills (comma-separated)'}
                  </label>
                  <input
                    type="text"
                    value={generationParams.subskills}
                    onChange={(e) => setGenerationParams({...generationParams, subskills: e.target.value})}
                    placeholder="e.g., free-body-diagram, net-force-sign"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Zorluk' : 'Difficulty'}
                  </label>
                  <select
                    value={generationParams.difficulty}
                    onChange={(e) => setGenerationParams({...generationParams, difficulty: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="easy">{language === 'tr' ? 'Kolay' : 'Easy'}</option>
                    <option value="medium">{language === 'tr' ? 'Orta' : 'Medium'}</option>
                    <option value="hard">{language === 'tr' ? 'Zor' : 'Hard'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Çift Sayısı' : 'Number of Pairs'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={generationParams.n_pairs}
                    onChange={(e) => setGenerationParams({...generationParams, n_pairs: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleGeneratePairs}
                disabled={generating || !generationParams.topic}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 transition-all duration-300 font-semibold"
              >
                {generating 
                  ? (language === 'tr' ? '⏳ Üretiliyor...' : '⏳ Generating...') 
                  : (language === 'tr' ? '✨ Soru Çifti Üret' : '✨ Generate Question Pairs')
                }
              </button>

              {generationError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                  ❌ {generationError}
                </div>
              )}
            </div>

            {/* Generated Pairs Preview */}
            {generatedPairs.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-gray-800">
                  {language === 'tr' ? '📝 Üretilen Soru Çiftleri' : '📝 Generated Question Pairs'}
                </h3>
                
                {generatedPairs.map((pair, index) => (
                  <div key={pair.pair_id} className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800">
                          {language === 'tr' ? 'Çift' : 'Pair'} #{index + 1}: {pair.concept_summary}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">ID: {pair.pair_id}</p>
                      </div>
                      <button
                        onClick={() => handleSubmitGeneratedPair(pair)}
                        disabled={loading}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                      >
                        {language === 'tr' ? '✓ Onayla & Gönder' : '✓ Approve & Submit'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* TYT Item */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                        <h5 className="font-semibold text-blue-800 mb-3">🇹🇷 TYT (Türkçe MCQ)</h5>
                        <p className="text-sm text-gray-700 mb-3">{pair.TYT_item.stem_TR}</p>
                        <div className="space-y-1 text-sm">
                          {pair.TYT_item.options.map((opt, i) => (
                            <div key={i} className={opt.startsWith(pair.TYT_item.correct_option) ? 'font-bold text-green-700' : 'text-gray-600'}>
                              {opt}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          ⏱️ {pair.TYT_item.time_sec}s | 🎯 {pair.TYT_item.difficulty}
                        </p>
                      </div>

                      {/* AP Item */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                        <h5 className="font-semibold text-purple-800 mb-3">🇺🇸 AP (English FRQ)</h5>
                        <p className="text-sm text-gray-700 mb-3">{pair.AP_item.stem_EN}</p>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p className="font-semibold text-purple-700">{language === 'tr' ? 'Rubrik:' : 'Rubric:'}</p>
                          {pair.AP_item.rubric_points?.map((point, i) => (
                            <li key={i}>{point}</li>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          ⏱️ {pair.AP_item.time_sec}s | 🎯 {pair.AP_item.difficulty}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manual Entry Tab */}
        {activeTab === 'manual' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              ✍️ {language === 'tr' ? 'Manuel Soru Ekle' : 'Manual Question Entry'}
            </h2>
            
            <form onSubmit={handleSubmitManualQuestion} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Konu' : 'Subject'}
                  </label>
                  <select
                    value={manualQuestion.subject}
                    onChange={(e) => setManualQuestion({...manualQuestion, subject: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Physics">Physics</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Biology">Biology</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Sınav' : 'Exam'}
                  </label>
                  <select
                    value={manualQuestion.exam_context[0]}
                    onChange={(e) => setManualQuestion({...manualQuestion, exam_context: [e.target.value]})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="TYT">TYT</option>
                    <option value="AP">AP</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Dil' : 'Language'}
                  </label>
                  <select
                    value={manualQuestion.language}
                    onChange={(e) => setManualQuestion({...manualQuestion, language: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="TR">Türkçe</option>
                    <option value="EN">English</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Soru Metni' : 'Question Stem'}
                </label>
                <textarea
                  value={manualQuestion.stem}
                  onChange={(e) => setManualQuestion({...manualQuestion, stem: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              {manualQuestion.question_type === 'MCQ' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Seçenekler' : 'Options'}
                  </label>
                  {manualQuestion.options.map((opt, i) => (
                    <input
                      key={i}
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const newOpts = [...manualQuestion.options];
                        newOpts[i] = e.target.value;
                        setManualQuestion({...manualQuestion, options: newOpts});
                      }}
                      placeholder={`${String.fromCharCode(65 + i)}) ...`}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                    />
                  ))}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 transition-all duration-300 font-semibold"
              >
                {loading ? (language === 'tr' ? 'Gönderiliyor...' : 'Submitting...') : (language === 'tr' ? 'Onayla & Gönder' : 'Submit for Approval')}
              </button>
            </form>
          </div>
        )}

        {/* My Questions Tab */}
        {activeTab === 'my-questions' && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              📊 {language === 'tr' ? 'Gönderdiğim Sorular' : 'My Submitted Questions'}
            </h2>
            
            {mySubmissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>{language === 'tr' ? 'Henüz soru göndermemişsiniz.' : 'No questions submitted yet.'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {mySubmissions.map((q) => (
                  <div key={q.pair_id || Math.random()} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          q.status === 'approved' ? 'bg-green-100 text-green-800' :
                          q.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          q.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {q.status === 'approved' ? (language === 'tr' ? '✅ Onaylandı' : '✅ Approved') :
                           q.status === 'rejected' ? (language === 'tr' ? '❌ Reddedildi' : '❌ Rejected') :
                           q.status === 'pending_review' ? (language === 'tr' ? '⏳ İnceleme Bekliyor' : '⏳ Pending Review') :
                           (language === 'tr' ? 'Bekliyor' : 'Pending')}
                        </span>
                        {q.TYT_item?.subject && (
                          <span className="ml-2 text-sm text-gray-600">
                            TYT + AP | {q.TYT_item.subject}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {(q.submitted_at || q.created_at) && new Date(q.submitted_at || q.created_at).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    
                    {/* Pair title and concept */}
                    {q.concept_summary && (
                      <h4 className="font-semibold text-gray-900 mb-2">
                        📚 {q.concept_summary}
                      </h4>
                    )}
                    
                    {/* TYT Question Preview */}
                    {q.TYT_item && (
                      <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs font-semibold text-blue-700 mb-1">TYT (MCQ)</p>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {q.TYT_item.stem_TR}
                        </p>
                      </div>
                    )}
                    
                    {/* AP Question Preview */}
                    {q.AP_item && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs font-semibold text-purple-700 mb-1">AP (FRQ)</p>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {q.AP_item.stem_EN}
                        </p>
                      </div>
                    )}
                    
                    {/* Shared Skills */}
                    {q.shared_skills && q.shared_skills.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {q.shared_skills.slice(0, 3).map((skill, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherPortalEnhanced;
